import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, EventEmitter, Input, Output, inject
} from "@angular/core";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { ButtonDirective } from "../../../application/directives/button/button.directive";
import { InfoPanelHeaderDirective } from "../../directives/info-panel-header/info-panel-header.directive";
import { InfoPanelIconDirective } from "../../directives/info-panel-icon/info-panel-icon.directive";

/**
 * Persistent, in-page message box with an `error`, `warning` or `info` variant.
 *
 * Content slots:
 * - `[mgdInfoPanelIcon]` replaces the variant's default icon.
 * - `[mgdInfoPanelHeader]` is rendered on the icon's row. It is deliberately not a heading
 *   element, so pass plain text in a `<span>`.
 * - Set `noIcon` to hide the icon entirely: no default icon and no projected icon, even if one is
 *   given. The title row is then rendered only when a header or the dismiss button needs it, so a
 *   body-only panel has no empty row above its body.
 * - Default content is the body and may contain links, emphasis and several `<p>` lines.
 *
 * The consumer must import `InfoPanelIconDirective` / `InfoPanelHeaderDirective` for the
 * slot it uses, otherwise the panel cannot detect the slot.
 *
 * The host gets `role="alert"` for `error` and `role="status"` for `warning` and `info`.
 *
 * The dismiss button's id is unique per panel instance and is exposed as `dismissButtonId`; the
 * `.mat-dismiss-icon-button` class is shared by every panel's button.
 *
 * A dismissed panel hides itself through a host class. A consumer shows it again by calling
 * `resetDismissed()` through a `@ViewChild`.
 *
 * @example
 * ```html
 * <mgd-info-panel variant="error">
 *   <span mgdInfoPanelHeader>No retailers are associated with this campaign.</span>
 *   Please select a different Location Type.
 * </mgd-info-panel>
 *
 * <mgd-info-panel variant="info" noIcon>
 *   <span mgdInfoPanelHeader>Stores nearby</span>
 *   Shows stores within the selected radius.
 * </mgd-info-panel>
 *
 * <mgd-info-panel variant="warning" dismissible (dismissed)="onWarningDismissed()">
 *   <mat-icon mgdInfoPanelIcon>translate</mat-icon>
 *   <span mgdInfoPanelHeader>Language changed</span>
 *   <p>Please review defaults and overrides.</p>
 * </mgd-info-panel>
 * ```
 */
@Component({
    selector: "mgd-info-panel",
    templateUrl: "./info-panel.component.html",
    styleUrl: "./info-panel.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatIcon,
        MatIconButton,
        ButtonDirective
    ],
    host: {
        "[attr.role]": "variant === 'error' ? 'alert' : 'status'",
        "[class.error]": "variant === 'error'",
        "[class.warning]": "variant === 'warning'",
        "[class.info]": "variant === 'info'",
        "[class.dismissed]": "isDismissed"
    }
})
export class InfoPanelComponent {

    /**
     * Counter used to give each panel instance its own dismiss button id.
     */
    private static _nextId = 0;

    /**
     * Visual and semantic variant. Selects the default icon, the colour role and the host's
     * screen-reader role.
     */
    @Input() variant: "error" | "warning" | "info" = "info";

    /**
     * Whether a dismiss button is rendered.
     */
    @Input({ transform: coerceBooleanProperty }) dismissible = false;

    /**
     * Whether the icon is hidden. When set, neither the default icon nor a projected
     * `[mgdInfoPanelIcon]` is rendered.
     */
    @Input({ transform: coerceBooleanProperty }) noIcon: boolean = false;

    /**
     * Emits once when the user dismisses the panel.
     */
    @Output() readonly dismissed = new EventEmitter<void>();

    /**
     * Projected icon slot. Presence replaces the default icon.
     */
    @ContentChild(InfoPanelIconDirective) icon?: InfoPanelIconDirective;

    /**
     * Projected header slot. Presence drives whether the header wrapper is rendered.
     */
    @ContentChild(InfoPanelHeaderDirective) header?: InfoPanelHeaderDirective;

    /**
     * Id of this panel's dismiss button, unique per instance (`mat-dismiss-<n>-icon-button`) so several
     * dismissible panels can share a page without duplicate ids. Use the `.mat-dismiss-icon-button` class to
     * target the button of any panel.
     */
    readonly dismissButtonId = `mat-dismiss-${InfoPanelComponent._nextId++}-icon-button`;

    private readonly _cdRef = inject(ChangeDetectorRef);

    private _dismissed = false;

    /**
     * True after the user dismissed the panel, until `resetDismissed()` is called.
     */
    get isDismissed(): boolean {
        return this._dismissed;
    }

    /**
     * Name of the Material icon rendered when no icon is projected.
     */
    get defaultIcon(): string {
        return this.variant;
    }

    /**
     * Shows the panel again after it was dismissed.
     */
    resetDismissed(): void {
        this._dismissed = false;
        this._cdRef.markForCheck();
    }

    /**
     * Hides the panel and emits `dismissed`.
     */
    onDismissClick(): void {
        this._dismissed = true;
        this.dismissed.emit();
    }

}
